/**
 * Operator Labels and Metadata
 * 
 * Maps operator names to user-friendly labels and descriptions
 */

export const OPERATOR_LABELS = {
  // Equality operators
  '=': 'equals',
  '!=': 'not equals',
  '==': 'equals',
  '<>': 'not equals',
  
  // Comparison operators
  '>': 'greater than',
  '<': 'less than',
  '>=': 'greater than or equal',
  '<=': 'less than or equal',
  
  // String operators
  'contains': 'contains',
  'startsWith': 'starts with',
  'endsWith': 'ends with',
  'like': 'like',
  'iLike': 'like (case-insensitive)',
  
  // List operators
  'in': 'in',
  'notIn': 'not in',
  'between': 'between',
  
  // Unary operators
  'isEmpty': 'is empty',
  'isNotEmpty': 'is not empty',
  'isNull': 'is null',
  'isNotNull': 'is not null',

  // Date-relative operators (UI-only)
  moreThanDaysAgo: 'more than N days ago',
  lessThanDaysAgo: 'less than N days ago',

  // Date absolute convenience operator (UI-only, expands to >= / <=)
  between: 'between'
};

/**
 * Get user-friendly label for an operator
 * @param {string} operator - Operator name
 * @returns {string} User-friendly label
 */
export function getOperatorLabel(operator) {
  return OPERATOR_LABELS[operator] || operator;
}

/**
 * Get operator display text (for dropdown)
 * @param {string} operator - Operator name
 * @returns {string} Display text (e.g., "Type = (equals)")
 */
export function getOperatorDisplayText(operator, fieldLabel = '') {
  const label = getOperatorLabel(operator);
  if (fieldLabel) {
    return `${fieldLabel} ${operator} (${label})`;
  }
  return `${operator} (${label})`;
}

/**
 * Group operators by category for better organization
 */
export const OPERATOR_GROUPS = {
  equality: ['=', '!=', '==', '<>'],
  // Treat between as a comparison-style operator so it appears with other date comparisons
  comparison: ['>', '<', '>=', '<=', 'between'],
  string: ['contains', 'startsWith', 'endsWith', 'like', 'iLike'],
  list: ['in', 'notIn'],
  unary: ['isEmpty', 'isNotEmpty', 'isNull', 'isNotNull'],
  // Date-specific groupings (not currently used by OperatorSelector grouping,
  // but available for future enhancements or specialized UIs)
  dateRelative: ['moreThanDaysAgo', 'lessThanDaysAgo'],
  dateAbsolute: ['between']
};

/**
 * Get operators grouped by category
 * @param {string[]} operators - List of operator names
 * @returns {Object} Operators grouped by category
 */
export function groupOperators(operators) {
  const grouped = {
    equality: [],
    comparison: [],
    string: [],
    list: [],
    unary: []
  };
  
  operators.forEach(op => {
    for (const [category, ops] of Object.entries(OPERATOR_GROUPS)) {
      if (ops.includes(op)) {
        grouped[category].push(op);
        break;
      }
    }
  });
  
  return grouped;
}

